import { Box, Typography, useMediaQuery, useTheme, Skeleton, CircularProgress } from '@mui/material';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { BasePage } from './bases/BasePage';
import { useState, useMemo } from 'react';
import { useQuery } from '@hooks/useQuery';
import { AppRoutes } from '@consts/routes';
import { DatePreset, datePresetFns } from '@utils/dates';
import { useNavAssert } from '@hooks/useNavAssert';
import { useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';
import { useNameMapQuery, useLocationEmployeeRanksQuery } from '@features/redux/apis/org';
import { useEmployeeCutMaterialsQuery, useCutActivityQuery } from '@features/redux/apis/cut';
import { MaterialIdMap } from '@consts/materials';
import { Chart } from '@components/Chart';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';

import {
    colors,
    typography as typographyTokens,
    radii,
    shadows,
    TableCard,
    DataTable as DesignDataTable,
} from '@components/design-system';
import type { DataTableColumn } from '@components/design-system/components/Table/DataTable';
import { CustomDatePicker } from '@components/CustomDatePicker';

dayjs.extend(utc);

const EMPLOYEE_ACTIVITY_PAGE_SIZE = 20;

type CutActivityRow = {
    id: string;
    manufacturer: string;
    model: string;
    coverage: string;
    material: string;
    cutDate: string;
    syncDate: string;
    cutType: string;
    cutStatus: string;
};

export const EmployeeDetails = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const role = useSelector((state: RootState) => state.auth.Roles.role);
    const tz = useSelector((state: RootState) => state.timezone.tz);

    const queryParams = useQuery();
    const params = {
        startDate: queryParams.get('startDate'),
        endDate: queryParams.get('endDate'),
        employeeEmail: queryParams.get('email')!,
        locationId: +queryParams.get('locationId')!,
        from: queryParams.get('from'),
        employeeId: queryParams.get('employeeId'),
    };

    // Determine back navigation based on where user came from
    const backRoute = params.from === 'manage' && params.employeeId
        ? `${AppRoutes.Organizations}?type=employee&id=${encodeURIComponent(params.employeeId)}` 
        : AppRoutes.Employees;

    const decodedEmployeeEmail = decodeURIComponent(params.employeeEmail);

    const paramsDateRange =
        params.startDate && params.endDate
            ? {
                  startDate: decodeURIComponent(params.startDate),
                  endDate: decodeURIComponent(params.endDate),
              }
            : null;

    const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(
        paramsDateRange || datePresetFns[DatePreset.LastSevenDays]
    );

    const isValidEmployee = Boolean(params.employeeEmail && params.locationId);
    useNavAssert(isValidEmployee, AppRoutes.Employees, 'Unable to find employee - invalid email or location ID.');
    useNavAssert(
        role.locations.scope === '*' || role.locations.scope.includes(params.locationId),
        AppRoutes.Insights,
        'Insufficient permissions to view this employee.'
    );

    // Data queries
    const { data: nameMap, isFetching: loadingNames } = useNameMapQuery({});
    const { data: employeeRanks, isFetching: loadingRanks } = useLocationEmployeeRanksQuery({
        LocationID: params.locationId,
        ...dateRange,
    });
    const { data: employeeMaterials, isFetching: loadingMaterials } = useEmployeeCutMaterialsQuery({
        LocationID: params.locationId,
        EmployeeEmail: params.employeeEmail,
        ...dateRange,
    });

    const [pageNum, setPageNum] = useState(1);
    const { data: cutActivity, isFetching: loadingActivity } = useCutActivityQuery({
        ...dateRange,
        pageSize: EMPLOYEE_ACTIVITY_PAGE_SIZE,
        pageNum: pageNum - 1,
        employeeEmail: params.employeeEmail,
        locationIds: `${params.locationId}`,
        tz,
    });

    const employeeRank = useMemo(
        () => employeeRanks?.find(({ EmployeeEmail }) => EmployeeEmail === decodedEmployeeEmail)?.Rank,
        [employeeRanks, decodedEmployeeEmail]
    );

    const totalCuts = +(cutActivity?.[0]?.totalCutsInQuery as any) || 0;
    const pageCount = Math.ceil(totalCuts / EMPLOYEE_ACTIVITY_PAGE_SIZE) || 1;

    const tableRows: CutActivityRow[] = useMemo(
        () =>
            cutActivity?.map((cut, idx) => ({
                id: `${cut.dbCutId || idx}`,
                manufacturer: cut.manufacturer,
                model: cut.model,
                coverage: cut.coverage,
                material: MaterialIdMap[cut.materialId as keyof typeof MaterialIdMap]?.[0] || 'Unknown',
                cutDate: dayjs.utc(cut.cutDate).tz(tz).format('MM/DD/YYYY HH:mm'),
                syncDate: dayjs.utc(cut.syncDate).tz(tz).format('MM/DD/YYYY HH:mm'),
                cutType: cut.cutType,
                cutStatus: cut.cutStatus,
            })) || [],
        [cutActivity, tz]
    );

    const columns: DataTableColumn<CutActivityRow>[] = [
        { field: 'manufacturer', headerName: 'Manufacturer', flex: 1, minWidth: 120 },
        { field: 'model', headerName: 'Model', flex: 1, minWidth: 120 },
        { field: 'material', headerName: 'Material', width: 100 },
        { field: 'cutDate', headerName: 'Cut Date', width: 140 },
        { field: 'cutType', headerName: 'Type', width: 100 },
        { field: 'cutStatus', headerName: 'Status', width: 100 },
    ];

    const locationName = nameMap?.locations[params.locationId] || `Location #${params.locationId}`;

    return (
        <BasePage>
            {!isMobile && <BreadcrumbSubBar />}

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    backgroundColor: colors.neutral[100],
                    p: isMobile ? 2 : 3,
                }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box
                        component={RouterLink}
                        to={backRoute}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: colors.neutral[500],
                            textDecoration: 'none',
                            transition: 'color 150ms ease',
                            '&:hover': {
                                color: colors.neutral[900],
                            },
                        }}>
                        <ArrowBackIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography
                        sx={{
                            fontSize: isMobile ? typographyTokens.fontSize.xl : typographyTokens.fontSize['2xl'],
                            fontWeight: typographyTokens.fontWeight.bold,
                            color: colors.neutral[900],
                        }}>
                        Employee Details
                    </Typography>
                </Box>

                {isValidEmployee && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3 }}>
                        {/* Top Row: Employee Info + Stats */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                                gap: isMobile ? 2 : 3,
                            }}>
                            {/* Employee Info Card */}
                            <Box
                                sx={{
                                    backgroundColor: colors.neutral[0],
                                    borderRadius: `${radii.lg}px`,
                                    border: `1px solid ${colors.neutral[200]}`,
                                    p: 2.5,
                                    boxShadow: shadows.card,
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: '50%',
                                            backgroundColor: `${colors.primary[500]}15`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        <PersonOutlineOutlinedIcon sx={{ color: colors.primary[500], fontSize: 26 }} />
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: typographyTokens.fontSize.lg,
                                                fontWeight: typographyTokens.fontWeight.semibold,
                                                color: colors.neutral[900],
                                            }}>
                                            {decodedEmployeeEmail.split('@')[0]}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: typographyTokens.fontSize.sm,
                                                color: colors.neutral[500],
                                            }}>
                                            Employee
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <EmailOutlinedIcon sx={{ color: colors.neutral[400], fontSize: 18 }} />
                                        <Typography
                                            sx={{
                                                fontSize: typographyTokens.fontSize.sm,
                                                color: colors.neutral[700],
                                            }}>
                                            {decodedEmployeeEmail}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LocationOnOutlinedIcon sx={{ color: colors.neutral[400], fontSize: 18 }} />
                                        <Typography
                                            sx={{
                                                fontSize: typographyTokens.fontSize.sm,
                                                color: colors.neutral[700],
                                            }}>
                                            {loadingNames ? <Skeleton width={100} /> : locationName}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Rank Card */}
                            <Box
                                sx={{
                                    backgroundColor: colors.neutral[0],
                                    borderRadius: `${radii.lg}px`,
                                    border: `1px solid ${colors.neutral[200]}`,
                                    p: 2.5,
                                    boxShadow: shadows.card,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                }}>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${colors.primary[400]}, ${colors.primary[600]})`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: `0 4px 12px ${colors.primary[500]}40`,
                                    }}>
                                    {loadingRanks ? (
                                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                                    ) : (
                                        <Typography
                                            sx={{
                                                fontSize: typographyTokens.fontSize['2xl'],
                                                fontWeight: typographyTokens.fontWeight.bold,
                                                color: '#fff',
                                            }}>
                                            #{employeeRank || '?'}
                                        </Typography>
                                    )}
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: typographyTokens.fontSize.lg,
                                            fontWeight: typographyTokens.fontWeight.semibold,
                                            color: colors.neutral[900],
                                        }}>
                                        Location Rank
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: typographyTokens.fontSize.sm,
                                            color: colors.neutral[500],
                                        }}>
                                        By total # of cuts
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Date Range Picker */}
                            <Box
                                sx={{
                                    backgroundColor: colors.neutral[0],
                                    borderRadius: `${radii.lg}px`,
                                    border: `1px solid ${colors.neutral[200]}`,
                                    p: 2.5,
                                    boxShadow: shadows.card,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: typographyTokens.fontSize.sm,
                                        fontWeight: typographyTokens.fontWeight.medium,
                                        color: colors.neutral[600],
                                        mb: 1,
                                    }}>
                                    Date Range
                                </Typography>
                                <CustomDatePicker dateRange={dateRange} setDateRange={setDateRange} />
                            </Box>
                        </Box>

                        {/* Materials Chart */}
                        <Box
                            sx={{
                                backgroundColor: colors.neutral[0],
                                borderRadius: `${radii.lg}px`,
                                border: `1px solid ${colors.neutral[200]}`,
                                boxShadow: shadows.card,
                                overflow: 'hidden',
                            }}>
                            <Box
                                sx={{
                                    px: 2.5,
                                    py: 2,
                                    borderBottom: `1px solid ${colors.neutral[200]}`,
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: typographyTokens.fontSize.lg,
                                        fontWeight: typographyTokens.fontWeight.semibold,
                                        color: colors.neutral[900],
                                    }}>
                                    Cuts by Material
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 2.5,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: 200,
                                }}>
                                {loadingMaterials ? (
                                    <CircularProgress />
                                ) : !employeeMaterials?.length ? (
                                    <Typography sx={{ color: colors.neutral[500] }}>No material data available</Typography>
                                ) : (
                                    <Chart
                                        map={false}
                                        loading={false}
                                        hasData={Boolean(employeeMaterials?.length)}
                                        height={250}
                                        options={{
                                            chart: { type: 'pie', animation: false, height: 250, width: isMobile ? 320 : 500 },
                                            legend: {
                                                layout: 'vertical',
                                                align: 'center',
                                                verticalAlign: 'middle',
                                                padding: 20,
                                                x: isMobile ? 0 : 150,
                                                maxHeight: 250,
                                                labelFormat: '{name}: ({x}) <b>{y}%</b>',
                                                maxWidth: 200,
                                            },
                                            plotOptions: {
                                                pie: {
                                                    center: [isMobile ? '50%' : '40%', '50%'],
                                                    allowPointSelect: false,
                                                    cursor: 'pointer',
                                                    dataLabels: { enabled: false },
                                                    showInLegend: true,
                                                },
                                                series: { animation: { duration: 0 } },
                                            },
                                            title: '',
                                            credits: { enabled: false },
                                            tooltip: { pointFormat: '{point.name}: ({point.x}) <b>{point.y}%</b>' },
                                            series: [
                                                {
                                                    data:
                                                        employeeMaterials?.map(({ MaterialID, PercentOfTotalCuts, NumCuts }) => ({
                                                            name: MaterialIdMap[MaterialID]?.[0] || 'Unknown',
                                                            y: PercentOfTotalCuts,
                                                            x: NumCuts,
                                                        })) || [],
                                                },
                                            ],
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Cut Activity Table */}
                        <TableCard
                            title='Cut Activity'
                            sx={{ minHeight: 300 }}>
                            <Box sx={{ flex: 1, minHeight: 0 }}>
                                <DesignDataTable
                                    rows={tableRows}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                    totalRows={totalCuts}
                                    page={pageNum}
                                    pageSize={EMPLOYEE_ACTIVITY_PAGE_SIZE}
                                    onPageChange={setPageNum}
                                    loading={loadingActivity}
                                    height={400}
                                />
                            </Box>
                        </TableCard>
                    </Box>
                )}
            </Box>
        </BasePage>
    );
};
